import { apiClient } from '@/core/api/client';
import { ensureAnonSessionId } from '@/shared/utils/anonymousSession';

/**
 * Logs a swallowed telemetry failure.
 *
 * These beacons must never surface to the visitor — someone who clicked a shared
 * contact card is trying to look at a provider, not to be measured, and a broken
 * analytics call is not their problem. But swallowing silently makes the two
 * outcomes indistinguishable: a beacon that never fires and one that fires and
 * fails look identical from the outside, and the only symptom is an empty table
 * nobody can explain. The warning costs nothing and keeps the failure legible.
 */
const warnTelemetryFailed = (what: string, error: unknown): void => {
  console.warn(`[contact-card] ${what} was not recorded:`, error);
};

/**
 * Reports that a shared contact card's link was followed back into the app.
 *
 * Best-effort: the caller gets a resolved promise either way.
 */
export async function recordContactCardOpen(deliveryId: string): Promise<void> {
  try {
    await apiClient.post(`/contact-card-shares/${encodeURIComponent(deliveryId)}/opens`, {
      anonSessionId: ensureAnonSessionId(),
    });
  } catch (error) {
    warnTelemetryFailed(`open of delivery ${deliveryId}`, error);
  }
}

/**
 * Reports that a pro copied the card to send from their own email client.
 *
 * Best-effort like the open beacon: telemetry must never turn a successful copy
 * into a visible failure.
 */
export async function recordContactCardCopy(serviceProviderId: string): Promise<void> {
  try {
    await apiClient.post('/contact-card-shares/copies', { serviceProviderId });
  } catch (error) {
    warnTelemetryFailed(`copy of provider ${serviceProviderId}`, error);
  }
}
